/**
 * StatusBar - Header status bar with wallet and network info
 */
import React, { useState, useRef, useEffect } from 'react';
import { useWallet } from '@solana/wallet-adapter-react';
import { ChevronLeft } from 'lucide-react';
import { useNetwork } from '../../../WalletProvider';
import { useSolBalance } from '../../../hooks/useSolBalance';
import { useLocalStorage } from '../../../hooks/useLocalStorage';
import { useNavigationHistory } from '../../../hooks/useNavigationHistory';
import CustomWalletModal from '../WalletModal';
import logger from '../../../utils/logger';
import NetworkIndicator from './NetworkIndicator';
import WalletTile from './WalletTile';
import ConnectButton from './ConnectButton';
import WalletMenu from './WalletMenu';
import TierBadge from './TierBadge';

const StatusBar = () => {
  const { connected, disconnect, wallet } = useWallet();
  const [showWalletModal, setShowWalletModal] = useState(false);
  const [showWalletMenu, setShowWalletMenu] = useState(false);
  const menuRef = useRef(null);
  const { network } = useNetwork();
  const { balance, loading } = useSolBalance();
  const [launchMode] = useLocalStorage('pf-launch-mode', 'basic');
  const { goBack, currentView, depth } = useNavigationHistory();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowWalletMenu(false);
      }
    };
    if (showWalletMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showWalletMenu]);

  const handleDisconnect = async () => {
    try {
      await disconnect();
      setShowWalletMenu(false);
    } catch (error) {
      logger.error('Failed to disconnect', { error: error.message });
    }
  };

  const handleSwitchWallet = async () => {
    try {
      // Try native account switcher first (for Phantom, Solflare, etc.)
      if (wallet && wallet.adapter && wallet.adapter.connect) {
        try {
          // For Phantom and most wallets, calling connect() when already connected
          // triggers the wallet's native account switcher UI
          await wallet.adapter.connect();
          setShowWalletMenu(false);
          return; // Success!
        } catch (err) {
          // Native switcher not supported or failed, fall through to disconnect method
        }
      }
      
      // Fallback: Disconnect and reopen wallet modal
      // This allows switching between different wallet providers or accounts
      await disconnect();
      setShowWalletMenu(false);
      
      // Small delay to let disconnect complete
      setTimeout(() => {
        setShowWalletModal(true);
      }, 100);
      
    } catch (error) {
      logger.error('Failed to switch wallet', { error: error.message });
      setShowWalletMenu(false);
    }
  };

  return (
    <>
      <style>{`
        header .wallet-adapter-button {
          background: linear-gradient(135deg, #4CAF50 0%, #2E7D32 100%) !important;
          border-radius: 8px !important;
          padding: 10px 20px !important;
          font-size: 14px !important;
          font-weight: 600 !important;
          cursor: pointer !important;
          color: white !important;
        }
        header .wallet-adapter-button:hover {
          background: linear-gradient(135deg, #5CB85C 0%, #4CAF50 100%) !important;
        }
      `}</style>
      <header style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '72px', minHeight: '72px', maxHeight: '72px', paddingTop: 'env(safe-area-inset-top, 0px)', background: 'rgba(0, 0, 0, 0.95)', backdropFilter: 'blur(20px)', borderBottom: 'none', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 20px', gap: '16px', zIndex: 1000, boxSizing: 'border-box', overflow: 'visible', flexShrink: 0 }}>
        {/* Left side - Logo/Back */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flex: 1, minWidth: 0 }}>
          {depth > 0 && (
            <button onClick={goBack} aria-label="Go back" style={{ width: '36px', height: '36px', borderRadius: '10px', background: 'var(--bg-input)', border: '1px solid var(--border)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'all 0.2s ease' }}>
              <ChevronLeft size={20} style={{ color: 'var(--text-primary)' }} />
            </button>
          )}
          
          {depth > 0 && currentView ? (
            <div>
              <h1 style={{ margin: 0, fontSize: '15px', fontWeight: 600, color: 'var(--text-primary)', lineHeight: 1.2 }}>{currentView.title}</h1>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
              <div style={{ fontFamily: "'Bebas Neue', 'Roboto Condensed', sans-serif", fontSize: '22px', fontWeight: 700, letterSpacing: '1.5px', background: 'linear-gradient(135deg, var(--accent-green) 0%, var(--accent-orange) 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text', textTransform: 'uppercase', lineHeight: 1, position: 'relative', whiteSpace: 'nowrap' }}>
                yobitch.fun
              </div>
              <TierBadge launchMode={launchMode} />
            </div>
          )}
        </div>

        {/* Right side - Network & Wallet */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flexShrink: 0, alignSelf: 'stretch' }}>
          <NetworkIndicator network={network} />
          
          {connected ? (
            <div ref={menuRef} style={{ position: 'relative', width: '39px', height: '39px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <WalletTile balance={balance} loading={loading} onClick={() => setShowWalletMenu(!showWalletMenu)} />
              {showWalletMenu && <WalletMenu onSwitchWallet={handleSwitchWallet} onDisconnect={handleDisconnect} />}
            </div>
          ) : (
            <ConnectButton onClick={() => setShowWalletModal(true)} />
          )}
        </div>
      </header>
      
      <CustomWalletModal
        visible={showWalletModal}
        onClose={() => setShowWalletModal(false)}
      />
    </>
  );
};

export default StatusBar;
